import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import api, { BACKEND_URL } from "../lib/api";
import { useSEO } from "../lib/seo";
import BlurHashImage from "../components/BlurHashImage";
import ShareButton from "../components/ShareButton";

const HOME_PRODUCT_IMAGES = {
  "Custom T-Shirts": "/custom-tshirts-home.jpg",
  "Oversized T-Shirts": "/oversized-tshirts-home.jpg",
  "Custom Hoodies": "/hoodies-home.jpg",
  "Sports Jerseys": "/sports-jerseys-home.png",
  "Corporate T-Shirts": "/corporate-tshirts-home.png",
  "College T-Shirts": "/college-tshirts-home.png",
  "Couple T-Shirts": "/couple-tshirts-home.png",
  "Family T-Shirts": "/family-tshirts-home.png",
};

const CATEGORY_FILTERS = [
  { label: "All", value: "all" },
  { label: "T-Shirts", value: "T-Shirts" },
  { label: "Oversized", value: "Oversized" },
  { label: "Hoodies", value: "Hoodies" },
  { label: "Jerseys", value: "Sports Jerseys" },
  { label: "Corporate", value: "Corporate T-Shirts" },
  { label: "College", value: "College T-Shirts" },
  { label: "Couple", value: "Couple T-Shirts" },
  { label: "Family", value: "Family T-Shirts" },
];

function normalizeSearchText(value) {
  return String(value || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/t[\s-]?shirts?/g, " tshirt ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
}

function searchMatches(query, values) {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) return true;
  const haystack = normalizeSearchText(values.filter(Boolean).join(" "));
  const words = normalizedQuery.split(" ").filter(Boolean);
  return words.every((word) => haystack.includes(word));
}

function isGiftProduct(product) {
  const type = String(product?.product_type || "").trim().toLowerCase();
  const identity = `${String(product?.category || "")} ${String(product?.name || "")} ${String(product?.slug || "")}`.toLowerCase();
  if (type === "gift") return true;
  if (type === "apparel" || type === "clothing") return false;
  if (/t-?shirts?|hoodies?|jerseys?|corporate wear|college wear|couple wear|family wear|oversized/i.test(identity)) return false;
  return /gift|mug|tumbler|key.?chain|photo tile|photo plate|photo plaque|acrylic|pillow|cushion|keepsake|personalised|personalized/i.test(identity);
}

function priceLabel(p) {
  if (p == null) return "Get Quote";
  return `₹${Number(p).toLocaleString("en-IN")}`;
}

function productImage(product) {
  const image = product.image || HOME_PRODUCT_IMAGES[product.name];
  if (!image || /^(https?:|data:|blob:)/i.test(image)) return image || "";
  // Uploaded product images are served by the backend; keep local category art on the frontend.
  if (image.startsWith("/uploads/") || image.startsWith("/api/") || image.startsWith("/storage/")) {
    return `${BACKEND_URL}${image}`;
  }
  return image;
}

function pricingGroupForProduct(product) {
  const category = String(product?.category || "").trim().toLowerCase();
  const slug = String(product?.slug || "").trim().toLowerCase();
  const name = String(product?.name || "").trim().toLowerCase();
  const identity = `${category} ${slug} ${name}`;

  if (
    slug === "custom-t-shirts" ||
    category.includes("t-shirt") ||
    category.includes("tshirt") ||
    category.includes("oversized")
  ) {
    return "T-Shirts";
  }
  if (identity.includes("hoodie")) return "Hoodies";
  if (identity.includes("jersey")) return "Sports Jerseys";
  if (identity.includes("corporate")) return "Corporate";
  if (identity.includes("college")) return "College";
  if (identity.includes("couple")) return "Couple";
  if (identity.includes("family")) return "Family";
  return null;
}

function productBasePrice(product, customPricing) {
  const configuredPrice = Number(product?.price);
  if (Number.isFinite(configuredPrice) && configuredPrice > 0) {
    return configuredPrice;
  }

  const qualities = Array.isArray(product?.qualities) ? product.qualities : [];
  const firstQuality = qualities.find((quality) => {
    const value = typeof quality === "object" ? quality?.price : null;
    return Number.isFinite(Number(value)) && Number(value) > 0;
  });
  if (firstQuality) return Number(firstQuality.price);

  const group = pricingGroupForProduct(product);
  const styles = customPricing?.pricing?.[group]?.styles;
  if (styles && typeof styles === "object") {
    const firstStylePrice = Object.values(styles).find((value) => Number.isFinite(Number(value)) && Number(value) > 0);
    if (firstStylePrice != null) return Number(firstStylePrice);
  }

  return null;
}

export default function Shop() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [products, setProducts] = useState([]);
  const [customPricing, setCustomPricing] = useState(null);
  const requestedCategory = searchParams.get("category");
  const normalizedRequestedCategory = String(requestedCategory || "").trim();
  const isGiftShop = /^(personalised gifts|personalized gifts|gifts|mugs & tumblers|keychains|photo gifts|acrylic gifts|photo plaques|photo tiles|photo plates|pillows)$/i.test(normalizedRequestedCategory);
  const initialCategory = isGiftShop
    ? (/^(personalised gifts|personalized gifts|gifts)$/i.test(normalizedRequestedCategory) ? "all" : normalizedRequestedCategory)
    : CATEGORY_FILTERS.find((item) => item.value.toLowerCase() === normalizedRequestedCategory.toLowerCase() || item.label.toLowerCase() === normalizedRequestedCategory.toLowerCase())?.value || (requestedCategory ? normalizedRequestedCategory : "all");
  const [activeCategory, setActiveCategory] = useState(initialCategory);

  useEffect(() => {
    setActiveCategory(initialCategory);
  }, [initialCategory]);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("featured");
  const giftCategories = useMemo(() => [...new Set(products.filter((product) => isGiftProduct(product) && String(product.category || "").trim()).map((product) => String(product.category).trim()))].sort((a, b) => a.localeCompare(b)), [products]);
  const categoryFilters = useMemo(() => {
    if (!isGiftShop) return CATEGORY_FILTERS;
    const shortcuts = [
      { label: "Mugs & Tumblers", value: "Mugs & Tumblers" },
      { label: "Keychains", value: "Keychains" },
      { label: "Photo Gifts", value: "Photo Gifts" },
      { label: "Acrylic Gifts", value: "Acrylic Gifts" },
    ];
    const shortcutValues = new Set(shortcuts.map((item) => item.value.toLowerCase()));
    const dynamicCategories = giftCategories
      .filter((category) => !shortcutValues.has(category.toLowerCase()))
      .map((category) => ({ label: category, value: category }));
    return [{ label: "All Gifts", value: "all" }, ...shortcuts, ...dynamicCategories];
  }, [giftCategories, isGiftShop]);

  useSEO({
    title: isGiftShop ? "Shop Personalised Gifts — PrintMyTees" : "Shop Custom Apparel & Personalised Gifts — PrintMyTees",
    description: isGiftShop ? "Browse personalised mugs, tumblers, keychains, photo gifts, acrylic keepsakes and more." : "Browse custom T-shirts, oversized tees, hoodies, jerseys and personalised apparel from PrintMyTees.",
  });

  useEffect(() => {
    Promise.all([
      api.get("/products"),
      api.get("/custom-pricing"),
    ])
      .then(([productsResponse, pricingResponse]) => {
        const r = productsResponse;
        const pricing = pricingResponse.data;
        setCustomPricing(pricing && typeof pricing === "object" ? pricing : null);
        const data = r.data;
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.products)
            ? data.products
            : [];
        setProducts(list);
      })
      .catch(() => {
        setProducts([]);
        setCustomPricing(null);
      });
  }, []);

  const filteredProducts = useMemo(() => {
    const term = search.trim();
    return products.filter((p) => {
      const productCategory = String(p.category || "").trim().toLowerCase();
      const productIdentity = `${productCategory} ${String(p.slug || "").toLowerCase()} ${String(p.name || "").toLowerCase()} ${String(p.product_type || "").toLowerCase()}`;
      const isGift = isGiftProduct(p);
      const categoryMatch =
        activeCategory === "all" ||
        (isGiftShop && activeCategory === "Mugs & Tumblers"
          ? isGift && /mug|tumbler/i.test(productIdentity)
          : isGiftShop && activeCategory === "Keychains"
            ? isGift && /key.?chain/i.test(productIdentity)
            : isGiftShop && activeCategory === "Photo Gifts"
              ? isGift && /photo|plaque|tile|plate|pillow|cushion/i.test(productIdentity)
              : isGiftShop && activeCategory === "Acrylic Gifts"
                ? isGift && /acrylic/i.test(productIdentity)
                : giftCategories.includes(activeCategory)
                  ? isGift && productCategory === activeCategory.toLowerCase()
                  : activeCategory === "T-Shirts"
                    ? normalizeSearchText(productIdentity).includes("tshirt") && !/oversized|couple|family|corporate|college/.test(normalizeSearchText(productIdentity))
                    : activeCategory === "Oversized"
                      ? productIdentity.includes("oversized")
                      : activeCategory === "Personalised Gifts"
                        ? isGift
                        : activeCategory === "Keychains"
                          ? /key.?chain/i.test(productIdentity)
                          : activeCategory === "Photo Plaques"
                            ? /plaque/i.test(productIdentity)
                            : activeCategory === "Photo Tiles"
                              ? /photo tile|tile/i.test(productIdentity)
                              : activeCategory === "Photo Plates"
                                ? /photo plate|plate/i.test(productIdentity)
                                : activeCategory === "Mugs & Tumblers"
                                  ? /mug|tumbler/i.test(productIdentity)
                                  : activeCategory === "Pillows"
                                    ? /pillow|cushion/i.test(productIdentity)
                                    : productCategory === activeCategory.toLowerCase());
      const searchMatch = searchMatches(term, [p.name, p.category, p.description, p.slug, p.product_type, ...(Array.isArray(p.colors) ? p.colors : []), ...(Array.isArray(p.tags) ? p.tags : [])]);
      const productTypeMatchesPage = isGiftShop ? isGift : !isGift;
      return productTypeMatchesPage && categoryMatch && searchMatch;
    });
  }, [products, activeCategory, search, giftCategories, isGiftShop]);

  const sortedProducts = useMemo(() => {
    const result = [...filteredProducts];

    if (sortBy === "price-low") {
      result.sort((a, b) => {
        const priceA = productBasePrice(a, customPricing);
        const priceB = productBasePrice(b, customPricing);
        if (priceA == null) return priceB == null ? 0 : 1;
        if (priceB == null) return -1;
        return priceA - priceB;
      });
    } else if (sortBy === "price-high") {
      result.sort((a, b) => {
        const priceA = productBasePrice(a, customPricing);
        const priceB = productBasePrice(b, customPricing);
        if (priceA == null) return priceB == null ? 0 : 1;
        if (priceB == null) return -1;
        return priceB - priceA;
      });
    } else if (sortBy === "name") {
      result.sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
    }

    return result;
  }, [filteredProducts, sortBy, customPricing]);

  return (
    <div className="min-h-screen bg-[#FAF7F1]" data-testid="shop-page">
      <section className="relative overflow-hidden bg-[#171615] text-white">
        <div className="pointer-events-none absolute inset-0 opacity-40" style={{ backgroundImage: "radial-gradient(ellipse at 82% 18%, rgba(217,83,40,.22), transparent 38%), linear-gradient(135deg, transparent 60%, rgba(255,255,255,.025) 60%, transparent 61%)" }} />
        <div className="container-pmt relative py-8 sm:py-11 lg:py-14">
      <div className="text-[9px] font-bold uppercase tracking-[0.24em] text-[#F17A45] sm:text-[10px]">PrintMyTees <span className="mx-2 text-white/35">/</span> The collection</div>
          <div className="mt-3 flex flex-col gap-5 sm:mt-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h1 className="max-w-3xl text-4xl font-black leading-[1.08] tracking-[-0.015em] sm:text-5xl sm:leading-[1.1] sm:tracking-[-0.02em] lg:text-6xl">{isGiftShop ? "Gifts with meaning." : "Find your fit."} <span className="text-[#F17A45]">{isGiftShop ? "Made personal." : "Make it yours."}</span></h1>
              <p className="mt-3 max-w-xl text-sm leading-6 text-stone-300 sm:text-base sm:leading-7">{isGiftShop ? "Personalised mugs, tumblers, keychains and photo gifts for your people and your special moments." : "Custom apparel and personalised gifts, made for your style, your people, and your moments."}</p>
            </div>
            <div className="flex items-center gap-5 border-t border-white/15 pt-4 lg:border-l lg:border-t-0 lg:pb-1 lg:pl-6 lg:pt-0">
              <div><div className="text-2xl font-bold sm:text-3xl">{products.length.toString().padStart(2, "0")}</div><div className="mt-1 text-[9px] font-bold uppercase tracking-[0.18em] text-stone-400">Products</div></div>
              <div className="h-9 w-px bg-white/15" />
              <div><div className="text-2xl font-bold text-[#F17A45] sm:text-3xl">{categoryFilters.length.toString().padStart(2, "0")}</div><div className="mt-1 text-[9px] font-bold uppercase tracking-[0.18em] text-stone-400">Collections</div></div>
            </div>
          </div>
        </div>
      </section>
      <div className="container-pmt py-6 sm:py-8">

      <div className="relative max-w-2xl">
        <SearchIcon />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={isGiftShop ? "Search personalised gifts…" : "Search products…"}
          aria-label="Search products"
          className="input-pmt min-h-12 rounded-xl border-[#E5DCCE] bg-white pl-10 pr-10 shadow-[0_4px_16px_rgba(40,30,20,.035)] focus:border-[#D95328]"
        />
        {search && <button type="button" onClick={() => setSearch("")} aria-label="Clear product search" title="Clear search" className="absolute right-2 top-1/2 -translate-y-1/2 min-w-9 min-h-9 inline-flex items-center justify-center text-stone-400 hover:text-stone-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D95328] focus-visible:ring-offset-1">×</button>}
      </div>

      <div className="mt-4 -mx-1 overflow-x-auto pb-2" aria-label={isGiftShop ? "Gift categories" : "Product categories"}>
        <div className="flex w-max gap-2 px-1">
          {categoryFilters.map((category) => {
            const active = activeCategory === category.value;
            return (
              <button
                key={category.value}
                type="button"
                onClick={() => setActiveCategory(category.value)}
                className={`whitespace-nowrap rounded-full border px-4 py-2.5 text-[11px] font-bold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D95328] focus-visible:ring-offset-2 ${active ? "border-[#171615] bg-[#171615] text-white shadow-sm" : "border-[#E5DCCE] bg-white text-stone-600 hover:border-[#D95328] hover:text-[#D95328]"}`}
                aria-pressed={active}
                title={`Show ${category.label.toLowerCase()} products`}
              >
                {category.label}
              </button>
            );
          })}
        </div>
      </div>

      {products.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-xs text-stone-500" aria-live="polite">
          <span>
            {filteredProducts.length} {filteredProducts.length === 1 ? "product" : "products"} found
          </span>
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-xs text-stone-600">
              <span>Sort by</span>
              <select
                aria-label="Sort products"
                value={sortBy}
                onChange={(event) => setSortBy(event.target.value)}
                className="min-h-10 rounded-lg border border-[#E5DCCE] bg-white px-3 text-sm"
              >
                <option value="featured">Featured</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="name">Name</option>
              </select>
            </label>
          {(search.trim() || activeCategory !== "all") && (
            <button
              type="button"
              onClick={() => {
                setSearch("");
                setActiveCategory("all");
              }}
              className="font-semibold text-[#D95328] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D95328] focus-visible:ring-offset-2 rounded"
            >
              Clear filters
            </button>
          )}
          </div>
        </div>
      )}

      {filteredProducts.length > 0 ? (
        <div className="mt-6 grid grid-cols-1 min-[390px]:grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-5">
          {sortedProducts.map((p) => {
            const giftProduct = isGiftProduct(p);
            const rawDiscount = Number(p.discount_percent ?? 0);
            const discount = Number.isFinite(rawDiscount) ? Math.min(100, Math.max(0, rawDiscount)) : 0;
            const basePrice = productBasePrice(p, customPricing);
            const salePrice = basePrice != null
              ? Math.round(basePrice * (1 - discount / 100))
              : null;
            return (
            <div key={p.slug} className="group relative min-w-0 overflow-hidden rounded-[18px] border border-[#E7DED2] bg-white shadow-[0_6px_20px_rgba(42,30,20,.045)] transition-all duration-300 hover:-translate-y-1 hover:border-[#D9B5A2] hover:shadow-[0_18px_36px_rgba(42,30,20,.11)] sm:rounded-[22px]">
            <Link
              to={`/shop/${p.slug}?from=${encodeURIComponent(`${location.pathname}${location.search}`)}`}
              className="flex h-full flex-col overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D95328] focus-visible:ring-offset-2"
              aria-label={`${giftProduct ? "View gift" : "Customize"} ${p.name}`}
              data-testid={`product-card-${p.slug}`}
            >
              <div className="relative aspect-[4/5] overflow-hidden border-b border-[#EEE6DC] bg-[radial-gradient(ellipse_at_50%_35%,#FFFFFF_0%,#F7F2EA_72%,#EEE5D9_100%)] p-2 sm:p-3">
                <div className="h-full overflow-hidden rounded-xl border border-white/80 bg-white shadow-[0_4px_18px_rgba(35,25,16,.04)]">
                {discount > 0 && <span className="absolute top-2 left-2 z-10 bg-[#D95328] text-white text-[10px] font-bold px-2 py-1">{discount}% OFF</span>}
                {productImage(p) ? (
                  <BlurHashImage
                    src={productImage(p)}
                    blurHash={HOME_PRODUCT_IMAGES[p.name] ? "" : p.blurhash}
                    alt={p.name}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-xs text-stone-500">
                    Image coming soon
                  </div>
                )}
                </div>
                <span className="pointer-events-none absolute bottom-4 left-4 rounded-full border border-[#E8DFD4] bg-white/90 px-2 py-1 text-[8px] font-bold uppercase tracking-[0.14em] text-stone-600 shadow-sm sm:bottom-5 sm:left-5 sm:text-[9px]">{giftProduct ? "Personalised gift" : "Made for your style"}</span>
              </div>

              <div className="flex flex-1 flex-col p-3.5 sm:p-4">
                <div
                  className="text-[15px] font-bold uppercase leading-tight tracking-[0.01em] text-[#171615] sm:text-lg"
                  style={{ fontFamily: "Barlow Condensed, sans-serif" }}
                >
                  {p.name}
                </div>
                <div className="mt-1.5 line-clamp-2 min-h-[2.25rem] text-[11px] leading-[1.45] text-stone-500 sm:text-xs">
                  {p.description || (giftProduct ? "A personalised gift made around your memories." : "Custom apparel made around your idea.")}
                </div>

                {!giftProduct && (p.sizes || []).length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {(p.sizes || []).slice(0, 6).map((s) => (
                      <span key={s} className="chip text-[10px] py-0.5 px-2">
                        {s}
                      </span>
                    ))}
                  </div>
                )}

                <div className="mt-3 flex flex-col items-start gap-2 border-t border-[#F0E9E0] pt-3 sm:flex-row sm:items-end sm:justify-between">
                  <span className="text-[11px] font-semibold leading-snug text-[#171615] sm:text-xs" data-testid={`product-price-${p.slug}`}>
                    {discount > 0 && basePrice != null && <span className="text-stone-400 line-through mr-2">{priceLabel(basePrice)}</span>}
                    {giftProduct || salePrice == null ? priceLabel(salePrice) : `Starting from ${priceLabel(salePrice)}`}
                  </span>
                  <span className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#171615] px-3 py-2.5 text-[9px] font-bold uppercase tracking-[0.14em] text-white transition-colors group-hover:bg-[#D95328] sm:w-auto sm:px-3.5">{giftProduct ? "View Gift" : "Customize"} <span aria-hidden="true">↗</span></span>
                </div>
              </div>
            </Link>
            <div className="absolute right-2 top-2 z-20" onClick={(event) => event.stopPropagation()}>
              <ShareButton title={p.name} url={`/shop/${p.slug}`} />
            </div>
            </div>
            );
          })}
        </div>
      ) : products.length === 0 ? (
        <div className="py-16 text-center text-stone-500" data-testid="shop-empty">
          <div className="font-semibold text-stone-800">We're preparing our collection.</div>
          <p className="mt-2 text-sm">Check back soon or contact us for a custom order.</p>
          <Link to={isGiftShop ? "/contact" : "/custom-orders"} className="btn-primary inline-flex mt-5">
            {isGiftShop ? "Request a Personalised Gift →" : "Custom Order →"}
          </Link>
        </div>
      ) : (
        <div className="py-16 text-center text-stone-500" data-testid="shop-filter-empty">
          <div className="font-semibold text-stone-800">No products in this category yet.</div>
          <p className="mt-2 text-sm">Try another category or clear your search to see more products.</p>
          <button
            type="button"
            onClick={() => {
              setSearch("");
              setActiveCategory("all");
            }}
            className="btn-secondary inline-flex mt-5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D95328] focus-visible:ring-offset-2"
          >
            Clear Filters
          </button>
        </div>
      )}
      </div>
    </div>
  );
}

function SearchIcon() {
  return (
    <svg aria-hidden="true" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400">
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  );
}
